/** Itemized Rehab Calculator table (v1 rehabRoiBlock's REHAB CALCULATOR half).
 * Check the fixes a property needs, tune rate/qty/size with thumb-friendly
 * steppers, and it totals live. The total mirrors into the canonical `rehab`
 * edit downstream. Collapsible so the drawer stays tidy; the header carries the
 * running total. Persists only after a real user interaction (v1 _calcUserActed
 * gate) so a mere render can never re-save stale defaults. */
import { useEffect, useMemo, useRef, useState } from "react";
import { usePhone } from "../../lib/usePhone";
import type { Lead } from "../../types";
import {
  REHAB_ITEMS, narrowRehabCalc, rehabTotal, rowOn, rowPrice, defaultHouse, defaultBldg, seedQty, usd,
  type RehabCalcState, type RehabRow, type RehabSize,
} from "./api";

const SIZES: RehabSize[] = ["Small", "Medium", "Large"];

export function RehabCalculator({
  lead,
  fields,
  onSave,
  startOpen = false,
  onTotal,
}: {
  lead: Lead;
  fields: Record<string, unknown> | undefined;
  onSave: (state: RehabCalcState, total: number) => void;
  startOpen?: boolean;
  /** Live total (fires on every change, incl. the seeded initial state) — the
   * Net ROI page uses it to drive Rehab Cost. Optional; the drawer omits it. */
  onTotal?: (total: number) => void;
}) {
  const saved = useMemo(() => narrowRehabCalc(fields?.["rehabCalc"]), [fields]);
  const [house, setHouse] = useState<number | null>(saved.house ?? defaultHouse(lead, fields));
  const [bldg, setBldg] = useState<number | null>(saved.bldg ?? defaultBldg(lead, fields));
  const [rows, setRows] = useState<Record<number, RehabRow>>(saved.rows);
  const dirty = useRef(false); // v1 _calcUserActed — never persist on mere render
  const debounce = useRef<ReturnType<typeof setTimeout> | null>(null);

  const state: RehabCalcState = { house, bldg, rows };
  const total = useMemo(() => rehabTotal(state, lead), [house, bldg, rows, lead]);

  // Surface the live total to the Net ROI page (drives Rehab Cost). Fires on the
  // seeded initial total too, so a saved sheet fills Rehab Cost on open.
  useEffect(() => { onTotal?.(total); }, [total]); // eslint-disable-line react-hooks/exhaustive-deps

  // persist (debounced) once the user has actually touched the sheet
  useEffect(() => {
    if (!dirty.current) return;
    if (debounce.current) clearTimeout(debounce.current);
    debounce.current = setTimeout(() => onSave({ house, bldg, rows }, rehabTotal({ house, bldg, rows }, lead)), 700);
    return () => {
      if (debounce.current) clearTimeout(debounce.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [house, bldg, rows]);

  const touch = () => { dirty.current = true; };
  const patchRow = (k: number, patch: Partial<RehabRow>) => {
    touch();
    setRows((prev) => ({ ...prev, [k]: { ...(prev[k] ?? {}), ...patch } }));
  };
  const toggle = (k: number, item: (typeof REHAB_ITEMS)[number]) =>
    patchRow(k, { on: !rowOn(item, rows[k] ?? {}) });
  const step = (k: number, field: "rate" | "qty", delta: number, current: number, min = 0) =>
    patchRow(k, { [field]: Math.max(min, Math.round((current + delta) * 100) / 100) });

  const houseN = house ?? 0;
  const bldgN = bldg ?? 0;
  const isPhone = usePhone();
  const onCount = REHAB_ITEMS.reduce((n, item, k) => n + (rowOn(item, rows[k] ?? {}) ? 1 : 0), 0);

  return (
    <div className="detailsec open">
      <div className="sechead static">
        <span>Rehab calculator</span>
        <span className="sub">{total > 0 ? usd(total) : "itemized"}</span>
      </div>
        <div className="rehabcalc">
          {/* Phone (Tom picked 1+2): the running total rides sticky at the top
              of the sheet — checking Kitchen Remodel moves a number you can
              SEE. Desktop keeps the bottom total row it always had. */}
          {isPhone ? (
            <div className="rehabstick">
              <span>{onCount} item{onCount === 1 ? "" : "s"} on</span>
              <strong>{usd(total)}</strong>
            </div>
          ) : null}
          <div className="rehabsqft">
            <label className="lbl">House sqft
              <input type="number" value={house ?? ""} placeholder="0"
                onChange={(e) => { touch(); setHouse(e.target.value === "" ? null : Number(e.target.value)); }} />
            </label>
            <label className="lbl">Building sqft
              <input type="number" value={bldg ?? ""} placeholder="0"
                onChange={(e) => { touch(); setBldg(e.target.value === "" ? null : Number(e.target.value)); }} />
            </label>
          </div>

          <div className="rehabrows">
            {REHAB_ITEMS.map((item, k) => {
              const row = rows[k] ?? {};
              const on = rowOn(item, row);
              const qty = row.qty ?? seedQty(item, lead);
              const price = rowPrice(item, row, houseN, bldgN, seedQty(item, lead));
              const rate = row.rate ?? item.rate ?? 0;
              const size = row.size ?? "Medium";
              const amt = row.amt ?? item.amt ?? 0;
              /* Phone + unchecked = one slim line (option 1). The controls and
                 computed price appear the moment it's checked — every kind
                 keeps its full editor (rate stepper, qty, S/M/L, amount);
                 nothing is dropped, it just doesn't pay rent while off. */
              if (isPhone && !on) {
                return (
                  <div key={k} className="rehabrow slim">
                    <label className="rehabname">
                      <input type="checkbox" checked={false} onChange={() => toggle(k, item)} />
                      <span className="rehabslimname">
                        <strong>{item.n}</strong>
                        <span className="rehabref">{item.ref}</span>
                      </span>
                    </label>
                  </div>
                );
              }
              return (
                <div key={k} className={"rehabrow" + (on ? " on" : "")}>
                  <label className="rehabname">
                    <input type="checkbox" checked={on} onChange={() => toggle(k, item)} />
                    <span>
                      <strong>{item.n}</strong>
                      <span className="rehabref">{item.ref}</span>
                    </span>
                  </label>
                  <div className="rehabctl">
                    {item.kind === "sqft" ? (
                      <span className="rehabstep">
                        <button type="button" onClick={() => step(k, "rate", -0.5, rate)}>−</button>
                        <input type="number" step={0.5} value={rate}
                          onChange={(e) => patchRow(k, { rate: Number(e.target.value) || 0 })} />
                        <button type="button" onClick={() => step(k, "rate", 0.5, rate)}>+</button>
                        <em>× {(item.base === "house" ? houseN : bldgN).toLocaleString()}</em>
                      </span>
                    ) : null}
                    {item.kind === "size" ? (
                      <>
                        <span className="rehabstep">
                          <button type="button" onClick={() => step(k, "qty", -1, qty)}>−</button>
                          <input type="number" value={qty}
                            onChange={(e) => patchRow(k, { qty: Number(e.target.value) || 0 })} />
                          <button type="button" onClick={() => step(k, "qty", 1, qty)}>+</button>
                        </span>
                        <select value={size} onChange={(e) => patchRow(k, { size: e.target.value as RehabSize })}>
                          {SIZES.map((s) => <option key={s} value={s}>{s}</option>)}
                        </select>
                      </>
                    ) : null}
                    {item.kind === "qty" ? (
                      <>
                        <span className="rehabstep">
                          <button type="button" onClick={() => step(k, "qty", -1, qty)}>−</button>
                          <input type="number" value={qty}
                            onChange={(e) => patchRow(k, { qty: Number(e.target.value) || 0 })} />
                          <button type="button" onClick={() => step(k, "qty", 1, qty)}>+</button>
                        </span>
                        <input className="rehabamt" type="number" value={amt}
                          onChange={(e) => patchRow(k, { amt: Number(e.target.value) || 0 })} />
                      </>
                    ) : null}
                    {item.kind === "flat" ? (
                      <input className="rehabamt" type="number" value={amt}
                        onChange={(e) => patchRow(k, { amt: Number(e.target.value) || 0 })} />
                    ) : null}
                  </div>
                  <span className={"rehabprice" + (on ? " on" : "")}>{usd(price)}</span>
                </div>
              );
            })}
          </div>

          <div className="rehabtotal">
            <span>Total rehab estimate</span>
            <strong>{usd(total)}</strong>
          </div>
        </div>
    </div>
  );
}
